import type { ComponentProps } from "react";
import {
  isPostPreviewSupported,
  type PreviewSupportPost,
  SkedPreview,
} from "@dialogueconsulting/sked-preview";
import { useInstagramPreviewUpliftOn } from "utils/feature-toggle-utils";
import "@dialogueconsulting/sked-preview/dist/style.css";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useTrackEvent } from "libs/analytics";

import { cn } from "@/lib/utils";

type SkedPostPreviewProps = ComponentProps<typeof SkedPreview> & {
  previewClassName?: string;
  previewSurface?: string;
};

const notifyCaptionCopySuccess = () =>
  toast.success("Caption copied to clipboard");
const notifyCaptionCopyError = () =>
  toast.error("Couldn’t copy caption. Select the text and copy it manually.");

export const SkedPostPreview = ({
  className,
  previewClassName,
  previewVariant,
  previewSurface,
  onCaptionCopyError,
  onCaptionCopySuccess,
  ...props
}: SkedPostPreviewProps) => {
  const trackEvent = useTrackEvent();
  // Insights has its own Redux store without the main app's common slice.
  // Page-derived analytics are a fallback; explicit surfaces work in either store.
  const page = useSelector(
    (state: { common?: { page?: string } }) => state.common?.page
  );
  const surface = previewSurface ?? (page?.trim().toLowerCase() || "preview");
  const handleCaptionCopySuccess = (caption: string) => {
    try {
      trackEvent({
        eventName: "preview_caption_copied",
        metadata: { surface },
      });
    } catch {
      // Analytics failures must not escape a successful clipboard operation.
    }
    (onCaptionCopySuccess ?? notifyCaptionCopySuccess)(caption);
  };
  const resolvedPreviewVariant = previewVariant ?? "uplift";
  /*
   * SKE-4975. THE client read of the rebuilt Instagram preview, for every
   * surface at once: this wrapper is what the composer, the calendar sheet,
   * the approvals drawer, the review tabs and Insights all render through.
   *
   * Reading it here rather than at each call site is deliberate. Five call
   * sites would be five chances to forget one, and a surface that forgot
   * would quietly disagree with the rest about what a customer sees.
   *
   * False until toggles arrive, which is the preview as it was.
   */
  const instagramPublishingChrome = useInstagramPreviewUpliftOn();
  const previewPost = props.post as PreviewSupportPost | null;
  const isPreviewSupported = isPostPreviewSupported(previewPost);
  const isOtherNotificationPreview =
    (previewPost?.platform || previewPost?.platformType) === "OT";
  const shouldFillFrame = !isPreviewSupported || isOtherNotificationPreview;

  return (
    <div
      className={cn(
        "relative mx-auto max-w-full overflow-hidden rounded-lg bg-white",
        shouldFillFrame ? "w-full" : "w-fit",
        className
      )}
      data-preview-frame-variant={resolvedPreviewVariant}
      data-testid="sked-post-preview-frame"
    >
      <SkedPreview
        {...props}
        instagramPublishingChrome={instagramPublishingChrome}
        onCaptionCopyError={onCaptionCopyError ?? notifyCaptionCopyError}
        onCaptionCopySuccess={handleCaptionCopySuccess}
        className={
          resolvedPreviewVariant === "uplift"
            ? cn("!rounded-none !border-0 !shadow-none", previewClassName)
            : undefined
        }
        previewVariant={resolvedPreviewVariant}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-[1] rounded-[inherit] border border-solid border-[#e6e6e6]"
      />
    </div>
  );
};
